import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthProvider';
import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { getVisibleBots } from '../../../utils/bots';
import { useBotPhotos } from '../../../hooks/useBotPhotos';

export const MAX_FLOWS = 50;

export const FOLDER_COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

function slugFromName(name) {
  const base = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  return `${base}_${Date.now()}`;
}

/**
 * Concentra estado, dados e ações da listagem de fluxos.
 * Os componentes de UI ficam apenas com apresentação e estado de formulário local.
 */
export function useFlowList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const bots = getVisibleBots(user?.bots);
  const photoUrls = useBotPhotos(bots);
  const botById = useMemo(() => {
    const map = {};
    bots.forEach(b => { map[b.id] = b; });
    return map;
  }, [bots]);

  const [flows, setFlows] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtro por pasta: 'all' | 'none' | id numérico da pasta
  const [folderFilter, setFolderFilter] = useState('all');

  useEffect(() => {
    fetchFlows();
  }, []);

  async function fetchFlows() {
    setLoading(true);
    try {
      const res = await authService.flows.listAll();
      setFlows(res.data?.flows || []);
      setFolders(res.data?.folders || []);
    } catch {
      showError('Erro ao carregar fluxos');
    } finally {
      setLoading(false);
    }
  }

  function defaultBot() {
    let storedId = null;
    try { storedId = localStorage.getItem('selectedBotId'); } catch { }
    return bots.find(b => String(b.id) === String(storedId)) || bots[0] || null;
  }

  function botLabelFor(flow) {
    const bot = botById[flow.bot_id];
    return bot?.bot_name || bot?.bot_username || flow.bot_name || flow.bot_username || `Bot #${flow.bot_id}`;
  }

  const noFolderCount = flows.filter(f => !f.folder_id).length;
  const folderCounts = useMemo(() => {
    const counts = {};
    flows.forEach(f => {
      if (f.folder_id) counts[f.folder_id] = (counts[f.folder_id] || 0) + 1;
    });
    return counts;
  }, [flows]);

  const visibleFlows = folderFilter === 'all'
    ? flows
    : folderFilter === 'none'
      ? flows.filter(f => !f.folder_id)
      : flows.filter(f => f.folder_id === folderFilter);

  /* ── Pastas ── */

  async function createFolder(name, color) {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const res = await authService.flows.folders.create(trimmed, color);
      const folder = res.data?.folder;
      if (folder) {
        setFolders(prev => [...prev, folder]);
        showSuccess(`Pasta "${folder.name}" criada`);
      }
      return folder || null;
    } catch (err) {
      showError(err?.response?.data?.error || 'Erro ao criar pasta');
      return null;
    }
  }

  async function saveFolder(folder, { name, color }) {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const res = await authService.flows.folders.update(folder.id, { name: trimmed, color });
      const updated = res.data?.folder;
      if (updated) {
        setFolders(prev => prev.map(f => f.id === updated.id ? updated : f));
      }
      return updated || null;
    } catch (err) {
      showError(err?.response?.data?.error || 'Erro ao atualizar pasta');
      return null;
    }
  }

  async function deleteFolder(folder) {
    const count = folderCounts[folder.id] || 0;
    const msg = count > 0
      ? `Excluir a pasta "${folder.name}"? Os ${count} fluxo(s) dela voltam para "Sem pasta".`
      : `Excluir a pasta "${folder.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await authService.flows.folders.delete(folder.id);
      setFolders(prev => prev.filter(f => f.id !== folder.id));
      setFlows(prev => prev.map(f => f.folder_id === folder.id ? { ...f, folder_id: null } : f));
      if (folderFilter === folder.id) setFolderFilter('all');
      showSuccess('Pasta excluída');
    } catch (err) {
      showError(err?.response?.data?.error || 'Erro ao excluir pasta');
    }
  }

  async function moveToFolder(flow, folderId) {
    try {
      await authService.flows.setFolder(flow.bot_id, flow.id, folderId);
      setFlows(prev => prev.map(f => f.id === flow.id ? { ...f, folder_id: folderId } : f));
      return true;
    } catch (err) {
      showError(err?.response?.data?.error || 'Erro ao mover fluxo');
      return false;
    }
  }

  /* ── Fluxos ── */

  async function toggleFlow(flow) {
    const nowActive = !flow.is_active;
    try {
      await authService.flows.save(flow.bot_id, {
        id: flow.id,
        name: flow.name,
        slug: flow.slug,
        is_active: nowActive,
        definition: flow.definition,
      });

      if (nowActive) {
        // Apenas um fluxo ativo por bot — desativa os demais do mesmo bot
        const others = flows.filter(f => f.id !== flow.id && f.bot_id === flow.bot_id && f.is_active);
        await Promise.all(others.map(f =>
          authService.flows.save(f.bot_id, {
            id: f.id, name: f.name, slug: f.slug,
            is_active: false, definition: f.definition,
          })
        ));
        setFlows(prev => prev.map(f => {
          if (f.id === flow.id) return { ...f, is_active: true };
          if (f.bot_id === flow.bot_id) return { ...f, is_active: false };
          return f;
        }));
      } else {
        setFlows(prev => prev.map(f => f.id === flow.id ? { ...f, is_active: false } : f));
      }

      showSuccess(`Fluxo "${flow.name}" ${nowActive ? 'ativado' : 'desativado'}`);
    } catch {
      showError('Erro ao atualizar fluxo');
    }
  }

  async function deleteFlow(flow) {
    if (!window.confirm(`Excluir o fluxo "${flow.name}"?`)) return;
    try {
      await authService.flows.delete(flow.bot_id, flow.id);
      setFlows(prev => prev.filter(f => f.id !== flow.id));
      showSuccess('Fluxo excluído');
    } catch {
      showError('Erro ao excluir fluxo');
    }
  }

  function editFlow(flow) {
    try { localStorage.setItem('selectedBotId', flow.bot_id); } catch { }
    navigate(`/bots/flows/${flow.bot_id}/edit?flowSlug=${flow.slug}`);
  }

  async function createFlow(bot, name) {
    const trimmed = name.trim();
    if (!trimmed || !bot) return false;
    if (flows.length >= MAX_FLOWS) {
      showError(`Limite de ${MAX_FLOWS} fluxos por usuário atingido.`);
      return false;
    }
    const slug = slugFromName(trimmed);
    try {
      await authService.flows.save(bot.id, { name: trimmed, slug, is_active: false, definition: {} });
      try { localStorage.setItem('selectedBotId', bot.id); } catch { }
      navigate(`/bots/flows/${bot.id}/edit?flowSlug=${slug}`);
      return true;
    } catch (err) {
      showError(err?.response?.data?.error || 'Erro ao criar fluxo');
      return false;
    }
  }

  async function fetchExportCode(flow) {
    try {
      const res = await authService.flows.exportFlow(flow.bot_id, flow.id);
      return res.data?.export_code || '';
    } catch {
      showError('Erro ao gerar código de exportação');
      return null;
    }
  }

  async function importFlow(bot, code, name) {
    const trimmed = code.trim();
    if (!trimmed || !bot) return null;
    try {
      const res = await authService.flows.importFlow(bot.id, trimmed, name.trim() || null);
      const imported = res.data?.flow;
      if (imported) {
        setFlows(prev => [{
          ...imported,
          bot_name: bot.bot_name,
          bot_username: bot.bot_username,
        }, ...prev]);
        showSuccess(`Fluxo "${imported.name}" importado com sucesso!`);
      }
      return imported || null;
    } catch (err) {
      showError(err?.response?.data?.error || 'Código inválido ou não encontrado');
      return null;
    }
  }

  return {
    // dados
    loading,
    bots,
    photoUrls,
    flows,
    folders,
    // filtro / derivados
    folderFilter,
    setFolderFilter,
    folderCounts,
    noFolderCount,
    visibleFlows,
    botLabelFor,
    defaultBot,
    // ações de pasta
    createFolder,
    saveFolder,
    deleteFolder,
    moveToFolder,
    // ações de fluxo
    toggleFlow,
    deleteFlow,
    editFlow,
    createFlow,
    fetchExportCode,
    importFlow,
  };
}
